// Plot definitions live in the project's own pyst, in a single /Randoms row:
// uniqueName identifies it, data2 holds the JSON. Same pattern the dashboard uses
// for digital twins (configuration.component.ts). Each project is a separate pyst
// backend, so plots are scoped to a project for free. Within a project the plots
// are grouped into tabs: data2 is `{ tabs: [{ id, name, plots }] }`. Rows written
// before tabs existed hold `{ plots }` (or a bare array) and load as a single tab.
import { Injectable } from '@angular/core';

import { BuilderState, Plot, PlotDraft, PlotTab, clampCols } from './plot.models';
import { PystService } from './pyst.service';

const UNIQUE_NAME = 'plot-workbench';
const DESCRIPTION = 'IoTBridge Plots: saved plot definitions';
const DEFAULT_TAB_NAME = 'Plots';

interface RandomsRow {
    '@iot.id'?: number | string;
    id?: number | string;
    uniqueName?: string;
    data2?: unknown;
}

@Injectable({ providedIn: 'root' })
export class PlotsService {
    /** Row id of the backing /Randoms entry, null until one exists. */
    private recordId: string | null = null;

    constructor(private pyst: PystService) {}

    async load(): Promise<PlotTab[]> {
        const filter = encodeURIComponent(`uniqueName eq '${UNIQUE_NAME}'`);
        const query = `/Randoms?$filter=${filter}&$select=id,uniqueName,data2&$orderby=id desc&$top=1`;
        const response = await this.pyst.get<{ value?: RandomsRow[] }>(query);
        const row = Array.isArray(response?.value) ? response.value[0] : undefined;

        if (!row) {
            this.recordId = null;
            return [this.newTab(DEFAULT_TAB_NAME)];
        }

        this.recordId = this.readId(row);
        return this.readTabs(row.data2);
    }

    async save(tabs: PlotTab[]): Promise<void> {
        const body = { uniqueName: UNIQUE_NAME, description: DESCRIPTION, data2: { tabs } };
        if (this.recordId) {
            await this.pyst.patch(`/Randoms(${this.entityKey(this.recordId)})`, body);
            return;
        }
        await this.pyst.post('/Randoms', body);
        // Pick up the id the backend assigned, so the next save patches in place.
        await this.load();
    }

    newPlot(draft: PlotDraft): Plot {
        return { ...draft, id: this.randomId() };
    }

    newTab(name: string, plots: Plot[] = []): PlotTab {
        return { id: this.randomId(), name: name.trim() || DEFAULT_TAB_NAME, plots };
    }

    private readTabs(data2: unknown): PlotTab[] {
        const raw = typeof data2 === 'string' ? this.parse(data2) : data2;
        const tabs = (raw as { tabs?: unknown })?.tabs;
        if (Array.isArray(tabs)) {
            const list = tabs
                .map((entry) => this.normalizeTab(entry))
                .filter((tab): tab is PlotTab => !!tab);
            return list.length ? list : [this.newTab(DEFAULT_TAB_NAME)];
        }
        // Pre-tabs row: everything it held becomes the first tab.
        return [this.newTab(DEFAULT_TAB_NAME, this.readPlots(raw))];
    }

    private normalizeTab(entry: unknown): PlotTab | null {
        if (!entry || typeof entry !== 'object') {
            return null;
        }
        const row = entry as Partial<PlotTab>;
        return {
            id: String(row.id ?? this.randomId()),
            name: String(row.name ?? '').trim() || DEFAULT_TAB_NAME,
            plots: this.readPlots(row.plots),
        };
    }

    private readPlots(raw: unknown): Plot[] {
        const list = Array.isArray(raw)
            ? raw
            : Array.isArray((raw as { plots?: unknown })?.plots)
                ? (raw as { plots: unknown[] }).plots
                : [];
        return list.map((entry) => this.normalize(entry)).filter((plot): plot is Plot => !!plot);
    }

    private parse(text: string): unknown {
        try {
            return JSON.parse(text);
        } catch {
            return null;
        }
    }

    private normalize(entry: unknown): Plot | null {
        if (!entry || typeof entry !== 'object') {
            return null;
        }
        const row = entry as Partial<Plot> & { forceHtml?: boolean; width?: unknown };
        let query = String(row.query ?? '').trim();
        if (!query) {
            return null;
        }
        // Older rows relied on the app stripping $as_json(true) at request time. That
        // toggle is gone — a query should say what it returns — so make the stored
        // text honest instead: same request pyst was actually receiving before.
        if (row.forceHtml !== false) {
            query = query.replace(/\$as_json\(\s*true\s*\)/gi, '$as_json(false)');
        }
        const size = (value: unknown, fallback: number): number => {
            const parsed = Number(value);
            return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : fallback;
        };
        // Rows from before the grid stored a pixel width; ~110px per column maps the
        // common 640px to half the page, which is what those plots looked like.
        const legacyWidth = Number(row.width);
        const cols = row.cols !== undefined
            ? clampCols(row.cols, 6)
            : Number.isFinite(legacyWidth) && legacyWidth > 0
                ? clampCols(legacyWidth / 110, 6)
                : 6;
        const builder = this.readBuilder(row.builder);
        return {
            id: String(row.id ?? this.randomId()),
            name: String(row.name ?? '').trim() || 'Untitled',
            query,
            ...(builder ? { builder } : {}),
            cols,
            height: size(row.height, 420),
            notes: String(row.notes ?? ''),
        };
    }

    /**
     * The builder's selection as stored. Hand-written and older plots have none,
     * and a malformed one is dropped rather than half-restored — the query is the
     * thing that must survive, this only decides whether the guided editor opens.
     */
    private readBuilder(raw: unknown): BuilderState | null {
        if (!raw || typeof raw !== 'object') {
            return null;
        }
        const row = raw as Record<string, unknown>;
        const streamIds = Array.isArray(row['streamIds'])
            ? row['streamIds'].map((id) => String(id)).filter((id) => id.length)
            : [];
        if (!streamIds.length) {
            return null;
        }
        const mode = row['mode'] === 'event' ? 'event' : 'stream';
        const threshold = row['threshold'];
        const thresholdRow = threshold && typeof threshold === 'object'
            ? (threshold as Record<string, unknown>)
            : null;
        const thresholdValue = Number(thresholdRow?.['value']);
        const count = Math.round(Number(row['eventCount']));
        const critical = Number(row['criticalYears']);
        return {
            mode,
            streamIds,
            presetLabel: row['presetLabel'] === undefined || row['presetLabel'] === null
                ? null
                : String(row['presetLabel']),
            threshold: thresholdRow && Number.isFinite(thresholdValue)
                ? {
                      value: thresholdValue,
                      direction: thresholdRow['direction'] === 'below' ? 'below' : 'above',
                  }
                : null,
            eventTypeId: row['eventTypeId'] === undefined || row['eventTypeId'] === null
                ? null
                : String(row['eventTypeId']),
            eventCount: Number.isFinite(count) && count > 0 ? count : 1,
            forecastView: row['forecastView'] === undefined || row['forecastView'] === null
                ? null
                : String(row['forecastView']),
            criticalYears: Number.isFinite(critical) ? critical : null,
            linkEvents: row['linkEvents'] !== false,
        };
    }

    private readId(row: RandomsRow): string | null {
        const value = row['@iot.id'] ?? row.id;
        if (typeof value === 'number' && Number.isFinite(value)) {
            return String(value);
        }
        const text = String(value ?? '').trim();
        return text || null;
    }

    private entityKey(id: string): string {
        return /^-?\d+$/.test(id) ? id : `'${id.replace(/'/g, "''")}'`;
    }

    private randomId(): string {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        return `plot-${Date.now()}-${Math.round(Math.random() * 100000)}`;
    }
}
